import { useCallback, useEffect, useRef, useState } from 'react';
import { useActorBoundary } from '@application/common/hooks/useActorBoundary';
import { quoteMatches, hasReadableTerms, safeHttpsUrl } from '@application/features/faqtoring/models';
import { FaqtoringApi } from '@infrastructure/faqtoring-api';
export function useInvoiceFaqtoring(invoiceId) {
    const boundary = useActorBoundary();
    const [state, setState] = useState();
    const [quote, setQuote] = useState();
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [uncertain, setUncertain] = useState(false);
    const [consentEpoch, setConsentEpoch] = useState(0);
    const [onboardingUrl, setOnboardingUrl] = useState();
    const sequence = useRef(0);
    const lock = useRef(false);
    const mounted = useRef(true);
    const selectedId = useRef(invoiceId);
    selectedId.current = invoiceId;
    const valid = (id, token) => mounted.current && selectedId.current === id && sequence.current === token && boundary.check();
    const refresh = useCallback(async () => {
        if (lock.current || !boundary.check())
            return;
        const token = ++sequence.current;
        const id = invoiceId;
        setLoading(true);
        setError('');
        setConsentEpoch(n => n + 1);
        setOnboardingUrl(undefined);
        try {
            const next = await FaqtoringApi.state(id);
            if (!valid(id, token))
                return;
            if (next.invoiceId !== id)
                throw new Error('Invoice context mismatch');
            setState(next);
            setQuote(next.quote || undefined);
            setUncertain(false);
            setOptions([]);
            if (next.eligible && next.allowedActions.includes('request_quote')) {
                const result = await FaqtoringApi.options(id);
                if (!valid(id, token))
                    return;
                setOptions(result.options || []);
            }
        }
        catch {
            if (valid(id, token)) {
                setState(undefined);
                setQuote(undefined);
                setError('error');
            }
        }
        finally {
            if (valid(id, token))
                setLoading(false);
        }
    }, [invoiceId, boundary.check]);
    useEffect(() => {
        mounted.current = true;
        setState(undefined);
        setQuote(undefined);
        setOptions([]);
        setError('');
        setUncertain(false);
        lock.current = false;
        setBusy(false);
        void refresh();
        return () => { mounted.current = false; sequence.current++; };
    }, [refresh]);
    const run = async (kind, optionId) => {
        if (lock.current || loading || uncertain || !boundary.check() || !state || state.invoiceId !== invoiceId)
            return;
        const action = kind === 'quote' ? 'request_quote' : kind;
        if (!state.allowedActions.includes(action))
            return;
        if (kind === 'consent' && (!quote || !quoteMatches(quote, invoiceId, Date.now()) || !hasReadableTerms(quote)))
            return;
        lock.current = true;
        setBusy(true);
        setError('');
        setConsentEpoch(n => n + 1);
        const token = ++sequence.current;
        const id = invoiceId;
        try {
            if (kind === 'quote') {
                if (!optionId || !options.some(x => x.serviceOptionId === optionId))
                    return;
                setQuote(undefined);
                const result = await FaqtoringApi.quote(id, optionId);
                if (!valid(id, token))
                    return;
                if (!quoteMatches(result, id, Date.now()))
                    throw new Error('Invalid quote context');
                setQuote(result);
                // The backend must advertise consent after storing this quote.
                const next = await FaqtoringApi.state(id);
                if (!valid(id, token))
                    return;
                if (next.invoiceId !== id)
                    throw new Error('Invoice context mismatch');
                setState(next);
            }
            else if (kind === 'onboarding') {
                const result = await FaqtoringApi.onboarding(id);
                if (!valid(id, token))
                    return;
                const url = safeHttpsUrl(result.url);
                if (!url || Date.parse(result.expiresAt) <= Date.now())
                    throw new Error('Invalid onboarding link');
                setOnboardingUrl(url);
            }
            else {
                const next = kind === 'consent' ? await FaqtoringApi.consent(id, quote) : await FaqtoringApi.cancel(id);
                if (!valid(id, token))
                    return;
                if (next.invoiceId !== id)
                    throw new Error('Invoice context mismatch');
                setState(next);
                setQuote(next.quote || quote);
                setOptions([]);
            }
        }
        catch (failure) {
            if (valid(id, token)) {
                const code = failure?.response?.data?.code;
                setError(code === 'PRICING_CONTRACT_ALIGNMENT_REQUIRED' ? 'pricingAlignmentRequired' : 'actionError');
                if (kind === 'consent' || kind === 'cancel')
                    setUncertain(true);
            }
        }
        finally {
            if (mounted.current && selectedId.current === id && sequence.current === token) {
                lock.current = false;
                setBusy(false);
            }
        }
    };
    return { state: state?.invoiceId === invoiceId ? state : undefined, quote, options, loading, busy, error, uncertain,
        accountChanged: boundary.changed, consentEpoch, onboardingUrl, refresh,
        requestQuote: (option) => run('quote', option), consent: () => run('consent'), cancel: () => run('cancel'), onboarding: () => run('onboarding'),
        dismissQuote: () => { setQuote(undefined); setConsentEpoch(n => n + 1); } };
}
